import React, { useEffect, useState, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

/**
 * Carrossel estiloso de capas das edições do guia impresso.
 * - Capa central: maior e em destaque
 * - Capas adjacentes: menores, levemente turvas e com escala reduzida
 * - Suporta arrasto/swipe no mobile e setas no desktop
 * - Se houver apenas 1 capa, mostra ela centralizada sem navegação
 */
export default function CityCoversCarousel({ covers = [], cityName = "" }) {
  const [active, setActive] = useState(0);
  const dragRef = useRef({ startX: 0, dragging: false });

  // Ordena por `order` (asc), depois `year` (desc se presente)
  const sorted = [...covers].sort((a, b) => {
    const oa = a.order ?? 0, ob = b.order ?? 0;
    if (oa !== ob) return oa - ob;
    return (b.year || 0) - (a.year || 0);
  });

  const total = sorted.length;
  const prev = useCallback(() => setActive((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setActive((i) => (i + 1) % total), [total]);

  // Teclado
  useEffect(() => {
    const k = (e) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [prev, next]);

  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX || e.touches?.[0]?.clientX || 0, dragging: true };
  };
  const onPointerUp = (e) => {
    if (!dragRef.current.dragging) return;
    const endX = e.clientX || e.changedTouches?.[0]?.clientX || 0;
    const delta = endX - dragRef.current.startX;
    if (Math.abs(delta) > 50) {
      if (delta < 0) next(); else prev();
    }
    dragRef.current.dragging = false;
  };

  if (total === 0) {
    return (
      <div className="text-center max-w-sm mx-auto p-10 bg-white/5 border border-white/10 rounded-3xl">
        <BookOpen size={42} className="mx-auto text-white/40"/>
        <p className="font-display text-lg font-bold text-white mt-4">Nenhuma edição publicada ainda</p>
        <p className="text-xs text-white/60 mt-2">As capas das edições impressas aparecerão aqui em breve.</p>
      </div>
    );
  }

  const current = sorted[active];

  return (
    <div className="relative w-full select-none" data-testid="city-covers-carousel">
      {/* Stage */}
      <div
        className="relative h-[68vh] min-h-[420px] max-h-[640px] flex items-center justify-center overflow-hidden touch-pan-y"
        onMouseDown={onPointerDown}
        onMouseUp={onPointerUp}
        onTouchStart={onPointerDown}
        onTouchEnd={onPointerUp}
      >
        {sorted.map((cv, i) => {
          // Distância relativa ao ativo (com wrap)
          let d = i - active;
          if (d > total / 2) d -= total;
          if (d < -total / 2) d += total;
          const abs = Math.abs(d);
          if (abs > 2) return null; // só renderiza 5 vizinhos

          const translate = d * 58; // % horizontal
          const scale = abs === 0 ? 1 : abs === 1 ? 0.78 : 0.6;
          const blur = abs === 0 ? 0 : abs === 1 ? 3 : 6;
          const opacity = abs === 0 ? 1 : abs === 1 ? 0.75 : 0.35;
          const zIndex = 10 - abs;

          return (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="absolute top-1/2 left-1/2 transition-all duration-500 ease-out cursor-pointer"
              style={{
                transform: `translate(-50%, -50%) translateX(${translate}%) scale(${scale})`,
                filter: `blur(${blur}px) saturate(${abs === 0 ? 1 : 0.85})`,
                opacity,
                zIndex,
              }}
              data-testid={`cover-slide-${i}`}
              aria-label={`Edição ${cv.edition || i + 1}`}
            >
              <div className="relative w-[280px] sm:w-[340px] aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-stone-200">
                <img src={cv.url} alt={`Capa ${cv.edition || i + 1}`} draggable={false} className="w-full h-full object-cover pointer-events-none"/>
                <div className="absolute inset-x-0 top-0 h-3 bg-gradient-to-r from-black/30 via-transparent to-transparent pointer-events-none"/>
                {abs === 0 && (
                  <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white">
                    <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{cityName}</p>
                    <p className="font-display text-lg font-bold leading-tight mt-0.5" data-testid="cover-active-edition">
                      {cv.edition || `Edição ${i + 1}`}
                    </p>
                    {cv.year && <p className="text-xs opacity-80 mt-0.5">{cv.year}</p>}
                  </div>
                )}
              </div>
            </button>
          );
        })}

        {/* Controles */}
        {total > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/15 shadow-lg transition-transform active:scale-90"
              data-testid="cover-prev"
              aria-label="Anterior"
            >
              <ChevronLeft size={22}/>
            </button>
            <button
              onClick={next}
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 z-20 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/15 shadow-lg transition-transform active:scale-90"
              data-testid="cover-next"
              aria-label="Próxima"
            >
              <ChevronRight size={22}/>
            </button>
          </>
        )}
      </div>

      {/* Indicadores + miniaturas */}
      {total > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap" data-testid="cover-thumbs">
          {sorted.map((cv, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`relative rounded-lg overflow-hidden border-2 transition-all ${i === active ? "border-amber-400 scale-110 shadow-lg shadow-amber-500/20" : "border-white/15 opacity-60 hover:opacity-100"}`}
              data-testid={`cover-thumb-${i}`}
              aria-label={`Ver edição ${cv.edition || i + 1}`}
            >
              <img src={cv.url} alt="" className="w-10 h-14 object-cover"/>
            </button>
          ))}
        </div>
      )}
      <p className="text-center text-xs text-white/60 mt-3" data-testid="cover-counter">
        {active + 1} de {total} {total === 1 ? "edição" : "edições"}
      </p>
    </div>
  );
}
